import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Paper,
  Fab,
  Tooltip,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { usePermissions } from 'react-admin';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PERMISSIONS } from '../../constants';
import { useSupportAgent } from '../../hooks/useSupportAgent';
import { getChainId } from '../../lib';
import FeatureIntro from '../FeatureIntro';

// Animações
const pulseRing = keyframes`
  0% { transform: scale(0.95); opacity: 0.7; }
  70% { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(0.95); opacity: 0; }
`;

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const sparkle = keyframes`
  0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
`;

const wiggle = keyframes`
  0%, 92%, 100% { transform: rotate(0deg); }
  94% { transform: rotate(-12deg); }
  96% { transform: rotate(12deg); }
  98% { transform: rotate(-6deg); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const typing = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-5px); }
`;

// Anel arco-íris girando atrás do FAB (vibe festiva)
const spinRing = keyframes`
  to { transform: rotate(360deg); }
`;

// Confete subindo
const confettiFloat = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 0; }
  15% { opacity: 1; }
  100% { transform: translateY(-52px) rotate(240deg); opacity: 0; }
`;

// Pílula de chamada entrando
const labelPop = keyframes`
  0% { transform: translateY(-50%) scale(0.7); opacity: 0; }
  60% { transform: translateY(-50%) scale(1.06); opacity: 1; }
  100% { transform: translateY(-50%) scale(1); opacity: 1; }
`;

// Brilho passando pela pílula
const shine = keyframes`
  0% { transform: translateX(-130%); }
  55%, 100% { transform: translateX(260%); }
`;

/**
 * Componentes do react-markdown estilizados pra caber dentro do balão de chat.
 * Mantém parágrafos curtos, headings discretos, blockquote com borda lateral,
 * listas com indent mínimo, code inline com background cinza.
 */
const markdownComponents = {
  p: ({ children }: any) => (
    <p style={{ margin: '0 0 8px 0', lineHeight: 1.6 }}>{children}</p>
  ),
  h1: ({ children }: any) => (
    <h3 style={{ margin: '12px 0 6px 0', fontSize: '1.05rem', fontWeight: 600 }}>{children}</h3>
  ),
  h2: ({ children }: any) => (
    <h4 style={{ margin: '12px 0 6px 0', fontSize: '1rem', fontWeight: 600 }}>{children}</h4>
  ),
  h3: ({ children }: any) => (
    <h5 style={{ margin: '10px 0 4px 0', fontSize: '0.95rem', fontWeight: 600 }}>{children}</h5>
  ),
  h4: ({ children }: any) => (
    <h6 style={{ margin: '8px 0 4px 0', fontSize: '0.9rem', fontWeight: 600 }}>{children}</h6>
  ),
  ul: ({ children }: any) => (
    <ul style={{ margin: '4px 0 8px 0', paddingLeft: '20px' }}>{children}</ul>
  ),
  ol: ({ children }: any) => (
    <ol style={{ margin: '4px 0 8px 0', paddingLeft: '20px' }}>{children}</ol>
  ),
  li: ({ children }: any) => (
    <li style={{ margin: '2px 0' }}>{children}</li>
  ),
  blockquote: ({ children }: any) => (
    <blockquote
      style={{
        margin: '6px 0',
        padding: '4px 10px',
        borderLeft: '3px solid rgba(102, 126, 234, 0.5)',
        background: 'rgba(102, 126, 234, 0.06)',
        borderRadius: '0 6px 6px 0',
        fontStyle: 'italic',
      }}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, children }: any) =>
    inline ? (
      <code
        style={{
          background: '#f0f0f0',
          padding: '1px 5px',
          borderRadius: '3px',
          fontSize: '0.88em',
          fontFamily: 'monospace',
        }}
      >
        {children}
      </code>
    ) : (
      <pre
        style={{
          background: '#1e1e1e',
          color: '#dcdcdc',
          padding: '8px 10px',
          borderRadius: '6px',
          overflowX: 'auto',
          fontSize: '0.82em',
          margin: '6px 0',
        }}
      >
        <code>{children}</code>
      </pre>
    ),
  table: ({ children }: any) => (
    <div style={{ overflowX: 'auto', margin: '6px 0' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.88em' }}>{children}</table>
    </div>
  ),
  th: ({ children }: any) => (
    <th style={{ border: '1px solid #ddd', padding: '4px 8px', background: '#fafafa', textAlign: 'left' }}>
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td style={{ border: '1px solid #ddd', padding: '4px 8px' }}>{children}</td>
  ),
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '8px 0' }} />,
  a: ({ children, href }: any) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'underline' }}>
      {children}
    </a>
  ),
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
  feedback?: 'positive' | 'negative' | null;
  // Mensagem de erro local: o id não é GUID e o backend recusa feedback (400)
  isError?: boolean;
  llmUsed?: string;
  contextsLoaded?: string[];
}

interface SupportChatProps {
  // Props opcionais
}

const SupportChat: React.FC<SupportChatProps> = () => {
  const { permissions } = usePermissions();
  const supportAgent = useSupportAgent();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [shouldRetry, setShouldRetry] = useState(false);
  const [lastUserQuestion, setLastUserQuestion] = useState<string | null>(null);
  const [fabEl, setFabEl] = useState<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Verifica permissão (use-support-agent OU admin)
  const hasPermission = permissions?.includes(PERMISSIONS.USE_SUPPORT_AGENT) ||
                        permissions?.includes(PERMISSIONS.ADMIN);

  // Scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Foca no input ao abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // Criar sessão ao abrir o chat
  const createSession = async () => {
    // Verifica se tem chainId antes de criar sessão
    const chainId = getChainId();
    if (!chainId) {
      const isAdmin = permissions?.includes(PERMISSIONS.ADMIN);
      const errorMessage = isAdmin
        ? '❌ Selecione uma rede na barra superior para usar o assistente de suporte.'
        : '❌ Não foi possível identificar a rede. Faça login novamente ou entre em contato com o suporte.';
      setMessages([{
        id: 'error',
        role: 'assistant',
        content: errorMessage,
        createdAt: new Date(),
      }]);
      return;
    }

    try {
      const response = await supportAgent.createSession();
      setSessionId(response.data.sessionId);
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: 'Olá! Sou o assistente de IA do PontuaX. Pergunte o que quiser! Por exemplo:\n\n- Por que um cliente não pontuou\n- Por que um cupom não aparece\n- Status de clientes, cupons, abastecimentos, etc.',
        createdAt: new Date(),
      }]);
    } catch (error: any) {
      console.error('Erro ao criar sessão:', error);
      const errorMsg = error?.body?.message || error?.message || 'Erro ao iniciar chat. Tente novamente.';
      setMessages([{
        id: 'error',
        role: 'assistant',
        content: `❌ ${errorMsg}`,
        createdAt: new Date(),
      }]);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!sessionId) {
      createSession();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewSession = () => {
    setSessionId(null);
    setMessages([]);
    setShouldRetry(false);
    setLastUserQuestion(null);
    createSession();
  };

  const sendMessage = async (messageToSend?: string) => {
    const questionText = messageToSend || input;
    if (!questionText.trim() || !sessionId || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: questionText,
      createdAt: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShouldRetry(false);
    setLastUserQuestion(questionText);

    try {
      const response = await supportAgent.sendMessage(sessionId, questionText);

      const assistantMessage: Message = {
        id: response.data.messageId,
        role: 'assistant',
        content: response.data.response,
        createdAt: new Date(),
        llmUsed: response.data.llmUsed,
        contextsLoaded: response.data.contextsLoaded,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: error?.message || 'Desculpe, ocorreu um erro. Tente novamente.',
        createdAt: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (messageId: string, feedback: 'positive' | 'negative') => {
    // Optimistic update - atualiza UI imediatamente
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, feedback } : m
    ));

    try {
      const response = await supportAgent.sendFeedback(messageId, feedback);

      if (response.data.shouldRetry) {
        setShouldRetry(true);
      }
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      // Não reverte o feedback na UI para não confundir o usuário
    }
  };

  const handleRetryWithMoreContext = () => {
    if (lastUserQuestion) {
      sendMessage(lastUserQuestion);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Não renderiza se não tem permissão
  if (!hasPermission) {
    return null;
  }

  return (
    <>
      {/* Botão flutuante + halos + sparkles + badge NOVO */}
      <FeatureIntro
        id="support-agent-launch-2026-06"
        title="🚀 Seu assistente de IA chegou!"
        description="Ele conhece o PontuaX inteiro e responde na hora. Pergunte o que quiser — por que um cliente não pontuou, como criar um desconto, o status de um cupom — e tenha a resposta em segundos, sem precisar abrir chamado."
        disabled={open}
        anchorEl={fabEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box ref={setFabEl} sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 1000, width: 72, height: 72 }}>
          {/* Anel arco-íris girando */}
          {!open && (
            <Box sx={{
              position: 'absolute', inset: -7, borderRadius: '50%',
              background: 'conic-gradient(from 0deg, #667eea, #ec4899, #f59e0b, #34d399, #667eea)',
              animation: `${spinRing} 6s linear infinite`,
              filter: 'blur(0.5px)',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 5px))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 5px))',
              pointerEvents: 'none',
            }} />
          )}

          {/* Halo expansivo triplo */}
          {!open && (
            <>
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                animation: `${pulseRing} 2.4s ease-out infinite`,
                pointerEvents: 'none',
              }} />
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b, #ec4899)',
                animation: `${pulseRing} 2.4s ease-out infinite`,
                animationDelay: '0.8s',
                pointerEvents: 'none',
              }} />
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'linear-gradient(135deg, #34d399, #667eea)',
                animation: `${pulseRing} 2.4s ease-out infinite`,
                animationDelay: '1.6s',
                pointerEvents: 'none',
              }} />
            </>
          )}

          {/* Sparkles multicolor */}
          {!open && [
            { top: -8, left: -6, size: 12, delay: '0s', c: '#fde68a' },
            { top: 6, right: -12, size: 9, delay: '0.6s', c: '#f9a8d4' },
            { bottom: -6, left: 14, size: 11, delay: '1.2s', c: '#a5b4fc' },
            { top: -4, right: 20, size: 8, delay: '1.8s', c: '#6ee7b7' },
            { bottom: 4, right: -8, size: 10, delay: '0.9s', c: '#fff' },
          ].map((p, i) => (
            <Box key={i} sx={{
              position: 'absolute', width: p.size, height: p.size,
              top: p.top, left: p.left, right: p.right, bottom: p.bottom,
              background: `radial-gradient(circle, #fff 0%, ${p.c} 60%, transparent 70%)`,
              borderRadius: '50%',
              animation: `${sparkle} 2.4s ease-in-out infinite`,
              animationDelay: p.delay,
              pointerEvents: 'none',
            }} />
          ))}

          {/* Confete subindo */}
          {!open && [
            { left: 8, c: '#f59e0b', delay: '0s', dur: '2.8s' },
            { left: 26, c: '#ec4899', delay: '0.7s', dur: '3.3s' },
            { left: 44, c: '#34d399', delay: '1.3s', dur: '2.6s' },
            { left: 58, c: '#667eea', delay: '1.9s', dur: '3.1s' },
          ].map((c, i) => (
            <Box key={`cf${i}`} sx={{
              position: 'absolute', bottom: 42, left: c.left,
              width: 6, height: 9, borderRadius: '1px',
              background: c.c,
              animation: `${confettiFloat} ${c.dur} ease-in infinite`,
              animationDelay: c.delay,
              pointerEvents: 'none',
            }} />
          ))}

          {/* Pílula de chamada — sempre visível, clicável */}
          {!open && (
            <Box
              onClick={handleOpen}
              sx={{
                position: 'absolute', top: '50%', right: '100%', mr: 1.5,
                transform: 'translateY(-50%)',
                px: 1.75, py: 0.75, borderRadius: '999px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #ec4899 100%)',
                backgroundSize: '200% 200%',
                color: 'white', fontWeight: 800, fontSize: '0.82rem',
                whiteSpace: 'nowrap', cursor: 'pointer', overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(118,75,162,0.45)',
                animation: `${labelPop} 0.5s ease-out, ${gradientShift} 4s ease infinite`,
                display: { xs: 'none', sm: 'block' },
                '&::before': {
                  content: '""',
                  position: 'absolute', top: 0, left: 0,
                  width: '45%', height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                  animation: `${shine} 3.5s ease-in-out infinite`,
                },
                '&:hover': { transform: 'translateY(-50%) scale(1.04)' },
                transition: 'transform 0.2s ease',
              }}
            >
              ✨ Fale com a IA
            </Box>
          )}

          <Tooltip title="✨ Novo! Assistente IA — pergunte o que quiser" placement="left" arrow>
            <Fab
              onClick={handleOpen}
              sx={{
                position: 'relative',
                width: 72, height: 72,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #ec4899 100%)',
                backgroundSize: '200% 200%',
                animation: !open
                  ? `${gradientShift} 4s ease infinite, ${wiggle} 5s ease-in-out infinite`
                  : 'none',
                boxShadow: '0 8px 32px rgba(102,126,234,0.6), 0 0 0 5px rgba(255,255,255,0.18)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #ec4899 50%, #667eea 100%)',
                  transform: 'scale(1.08)',
                },
                transition: 'transform 0.2s ease',
              }}
            >
              <BotIcon sx={{ fontSize: 36 }} />
            </Fab>
          </Tooltip>

        </Box>
      </FeatureIntro>

      {/* Drawer do Chat */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon />
            <Typography variant="h6" fontWeight="bold">
              Assistente de Suporte
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Nova conversa">
              <IconButton onClick={handleNewSession} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mensagens */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: '#f5f7fb',
          }}
        >
          {messages.map((message, index) => {
            // Verifica se é a última mensagem do assistente
            const isLastAssistantMessage = message.role === 'assistant' &&
              index === messages.map((m, i) => m.role === 'assistant' ? i : -1).filter(i => i >= 0).pop();

            return (
              <Fade in key={message.id} timeout={300}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                    animation: `${fadeInUp} 0.3s ease-out`,
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '85%',
                      borderRadius: message.role === 'user'
                        ? '20px 20px 4px 20px'
                        : '20px 20px 20px 4px',
                      background: message.role === 'user'
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'white',
                      color: message.role === 'user' ? 'white' : 'inherit',
                      boxShadow: message.role === 'user'
                        ? '0 4px 15px rgba(102, 126, 234, 0.3)'
                        : '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        lineHeight: 1.6,
                        '& p:last-child': { marginBottom: 0 },
                      }}
                    >
                      {message.role === 'user' ? (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span>
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{message.content}</ReactMarkdown>
                      )}
                    </Typography>
                  </Paper>

                  {/* Feedback buttons - só mostra na última mensagem do assistente, se não for welcome/error, e se ainda não deu feedback */}
                  <Fade in={isLastAssistantMessage && message.id !== 'welcome' && message.id !== 'error' && !message.isError && !isLoading && !message.feedback} timeout={300} unmountOnExit>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Ajudou?
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleFeedback(message.id, 'positive')}
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': { bgcolor: '#d1fae5', color: '#10b981', transform: 'scale(1.1)' },
                        }}
                      >
                        <ThumbUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleFeedback(message.id, 'negative')}
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': { bgcolor: '#fee2e2', color: '#ef4444', transform: 'scale(1.1)' },
                        }}
                      >
                        <ThumbDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Fade>

                  {/* Mostra confirmação após feedback */}
                  <Fade in={!!message.feedback} timeout={500}>
                    <Box sx={{
                      mt: 1,
                      display: message.feedback ? 'flex' : 'none',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: message.feedback === 'positive' ? '#d1fae5' : '#fee2e2',
                    }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: message.feedback === 'positive' ? '#059669' : '#dc2626',
                          fontWeight: 500,
                        }}
                      >
                        {message.feedback === 'positive' ? '👍 Obrigado!' : '👎 Vamos melhorar!'}
                      </Typography>
                    </Box>
                  </Fade>
                </Box>
              </Fade>
            );
          })}

          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '20px 20px 20px 4px',
                  bgcolor: 'white',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#667eea',
                        animation: `${typing} 1.4s infinite`,
                        animationDelay: `${i * 0.2}s`,
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Box>
          )}

          {/* Retry message */}
          {shouldRetry && lastUserQuestion && (
            <Fade in>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Posso tentar novamente com mais detalhes. Quer que eu busque mais informações?
                </Typography>
                <Box
                  component="button"
                  onClick={handleRetryWithMoreContext}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 1,
                    border: 'none',
                    borderRadius: 2,
                    bgcolor: '#f59e0b',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#d97706',
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                  Tentar novamente
                </Box>
              </Paper>
            </Fade>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'white',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Digite sua pergunta..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              inputRef={inputRef}
              disabled={isLoading || !sessionId}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  bgcolor: '#f5f7fb',
                  height: 44,
                  '&:hover': {
                    bgcolor: '#eef1f8',
                  },
                  '&.Mui-focused': {
                    bgcolor: 'white',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '10px 16px',
                },
              }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading || !sessionId}
              sx={{
                width: 44,
                height: 44,
                minWidth: 44,
                flexShrink: 0,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                },
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                },
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default SupportChat;
